/* Material's filled set, not lucide. lucide draws every glyph as an open
   stroke path, so there is no fill to turn on -- getting solid icons means
   a solid icon family, not a prop. Names map one-for-one to the outline
   icons they replace. */
import {
  MdSpaceDashboard,
  MdMenuBook,
  MdHowToReg,
  MdSchool,
  MdApartment,
  MdAutorenew,
  MdChat,
  MdNotifications,
  MdFactCheck,
  MdPalette,
  MdInsights,
  MdSmartToy,
} from 'react-icons/md';
import { withoutComingSoon } from '../../utils/comingSoon';

/* react-icons renders an <svg> that fills with currentColor, so the
   sidebar's colour rules keep working unchanged. `strokeWidth` is gone --
   a filled glyph has no stroke. The class is what sidebar.css targets. */
const ico = (Icon) => (
  <Icon size={19} className="lms-nav-icon" style={{ flex: '0 0 19px' }} />
);

const ALL_MENU = [
  {
    title: 'MAIN MENU',
    classsChange: 'menu-title',
    extraclass: 'first',
  },
  {
    title: 'Dashboard',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdSpaceDashboard),
    eventKey: 'dashboard',
    to: 'dashboard',
    content: [],
  },
  {
    title: 'LMS',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdMenuBook),
    eventKey: 'lms',
    content: [
      { title: 'Calendar', to: 'admin-calendar', eventKey: 'admin-calendar' },
      {
        title: 'Attendances',
        to: 'list-attendance',
        eventKey: 'attendance-module',
        // No role was ever granted `attendance-module`, so this entry only
        // showed for super admins. The page itself is gated on
        // `list-attendance`, so `menu` on that shows it too -- unless the
        // person already has the Trainer Module's Attendance link
        // (`add-attendance`), which goes to the same page.
        altEventKey: 'list-attendance',
        altUnlessKey: 'add-attendance',
      },
      // Beside Attendances, because it is what students said about the
      // sessions those records describe.
      {
        title: 'Session Feedback',
        to: 'session-feedback',
        eventKey: 'session-feedback',
      },
      { title: 'All Students', to: 'all-students', eventKey: 'all-students' },
      { title: 'Trainers', to: 'list-trainer', eventKey: 'list-trainer' },
      { title: 'Batches', to: 'batches', eventKey: 'batches' },
      { title: 'Classrooms', to: 'list-classroom', eventKey: 'list-classroom' },
      { title: 'Events', to: 'list-event-form', eventKey: 'list-event-form' },
      { title: 'Syllabus', to: 'list-syllabuses', eventKey: 'list-syllabuses' },
      { title: 'Course Modules', to: 'list-course', eventKey: 'list-course' },
      {
        title: 'Ranking Dashboard',
        to: 'ranking-dashboard',
        eventKey: 'ranking-dashboard',
      },
      { title: 'Students Report', to: 'students-report', eventKey: 'students-report' },
      { title: 'Batch Report', to: 'batchreport', eventKey: 'batchreport' },
    ],
  },
  {
    title: 'Trainer Module',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdHowToReg),
    eventKey: 'trainer-module',
    content: [
      {
        title: 'Trainer Dashboard',
        to: 'trainer-dashboard',
        eventKey: 'trainer-dashboard',
      },
      {
        title: 'Attendance',
        to: 'list-attendance',
        eventKey: 'add-attendance',
      },
      {
        title: 'CourseWiseMcq',
        to: 'course-wise-mcq',
        eventKey: 'course-wise-mcq',
      },
      { title: 'Quiz', to: 'trainer-listquiz', eventKey: 'trainer-listquiz' },
      {
        title: 'Calendar',
        to: 'trainer-calendar',
        eventKey: 'trainer-calendar',
      },
      {
        title: 'Quiz To Batch',
        to: 'list-quiz-to-batch',
        eventKey: 'list-quiz-to-batch',
      },
      {
        title: 'Ranking Dashboard',
        to: 'ranking-dashboard',
        eventKey: 'ranking-dashboard',
      },
      {
        title: 'Mock Interview',
        to: 'mock-interview',
        eventKey: 'mock-interview',
      },
    ],
  },
  {
    title: 'Student Module',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdSchool),
    eventKey: 'student-module',
    content: [
      {
        title: 'Student Dashboard',
        to: 'student-dashboard',
        eventKey: 'student-dashboard',
      },
      {
        title: 'Suggested Batches',
        to: 'suggested-batches',
        eventKey: 'student-dashboard',
      },
      {
        title: 'Student Profile',
        to: 'student-profile',
        eventKey: 'student-profile',
      },
      {
        title: 'Student ID Card',
        to: 'student-id-card',
        eventKey: 'student-id-card',
      },
      {
        title: 'Job Opening Details',
        to: 'job-section',
        eventKey: 'job-section',
      },
      { title: 'Certificate', to: 'certificate', eventKey: 'certificate' },
      {
        title: 'Calendar',
        to: 'attendance-calendar',
        eventKey: 'attendance-calendar',
      },
      {
        // The student's own attendance, present and absent, with its trend.
        // Rides on the calendar's permission (a student has it) and is
        // shown to students only -- see SideBar.jsx's studentOnly filter.
        title: 'My Attendance Report',
        to: 'my-attendance-report',
        eventKey: 'attendance-calendar',
        studentOnly: true,
      },
      {
        title: 'My Quizzes',
        to: 'student-quizzes',
        eventKey: 'student-quizzes',
      },
      {
        title: 'Mock Interview',
        to: 'student-mock-interview',
        eventKey: 'student-mock-interview',
      },
      {
        title: 'Ranking Dashboard',
        to: 'ranking-dashboard',
        eventKey: 'ranking-dashboard',
      },
      {
        title: 'Feedback',
        to: 'https://feedback.sevenmentor.com/feedback',
        eventKey: 'student-dashboard',
        external: true,
      },
    ],
  },
  // Each report is its own page (CrmReports.jsx); one grant opens both.
  {
    title: 'CRM Menu',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdInsights),
    eventKey: 'crm-reports',
    content: [
      { title: 'Student Report', to: 'crm-reports/students', eventKey: 'crm-reports' },
      { title: 'Batch Report', to: 'crm-reports/batches', eventKey: 'crm-reports' },
    ],
  },
  // Questions about the LMS data, answered by AI from the live database.
  {
    title: 'AI Board',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdSmartToy),
    eventKey: 'ai-board',
    to: '/ai-board',
    content: [],
  },
  {
    title: 'Classroom Management',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdApartment),
    eventKey: 'classroom-management',
    to: '/classroom-app',
    content: [],
  },
  {
    title: 'Batch Requests',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdAutorenew),
    // The permission module is named after the route, like every other page.
    eventKey: 'batch-requests',
    to: '/batch-requests',
    content: [],
  },
  // Gated on the `mockinterviewlist` module: admins/superadmins pass
  // hasPermission unconditionally, anyone else needs that module granted with
  // the `menu` operation on their profile.
  {
    title: 'Mock Interviews',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdFactCheck),
    eventKey: 'mockinterviewlist',
    to: '/mock-interviews',
    content: [],
  },
  // SideBar.jsx already carries everything this entry needs: it ungates the
  // 'chat' eventKey for students and trainers, admins and superadmins arrive
  // through hasPermission, and the unread badge is keyed off the same
  // eventKey. Only the entry itself was missing, so nobody could see it.
  {
    title: 'Chat',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdChat),
    eventKey: 'chat',
    to: '/chat',
    content: [],
  },
  // Kept last on purpose — every role has a notification inbox, so SideBar.jsx
  // shows this one unconditionally instead of gating it on a `menu` permission
  // that no role has been granted yet.
  {
    title: 'Notifications',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdNotifications),
    eventKey: 'notifications',
    to: '/notifications',
    content: [],
  },
  // Ungated for the same reason as Notifications: the palette is a
  // per-person preference held in their own browser, so there is no
  // role that should be unable to reach it.
  {
    title: 'Appearance',
    classsChange: 'mm-collapse',
    iconStyle: ico(MdPalette),
    eventKey: 'appearance',
    to: '/setting/appearance',
    content: [],
  },
];

/* The sidebar and the topbar search both read this, so a url listed in
   COMING_SOON_FEATURES (src/config/watermark.js) leaves both at once. */
export const MenuList = withoutComingSoon(ALL_MENU);

/* Who sees what in the sidebar. Kept here, beside the list it reads, because
   two places ask: the sidebar that draws it and the home page that sends a
   person with no dashboard to the first page they do have. */

// Entry keys listed under more than one group. Seeing one of these says
// nothing about which group a person belongs in.
const SHARED_ITEM_KEYS = (() => {
  const seen = new Map();
  MenuList.forEach((group) => {
    new Set((group.content || []).map((item) => item.eventKey)).forEach((key) =>
      seen.set(key, (seen.get(key) || 0) + 1)
    );
  });
  return new Set([...seen].filter(([, count]) => count > 1).map(([key]) => key));
})();

// May this person see this entry of a group?
export const canSeeMenuItem = (item, hasPermission, isStudent) =>
  hasPermission(item.eventKey, 'menu') ||
  // A second key the entry answers to -- see Attendances above.
  Boolean(
    item.altEventKey &&
      hasPermission(item.altEventKey, 'menu') &&
      !(item.altUnlessKey && hasPermission(item.altUnlessKey, 'menu'))
  ) ||
  item.eventKey === 'chat' ||
  Boolean(isStudent && item.eventKey === 'student-mock-interview');

/* A group opens for whoever holds its own key -- and also for whoever can
   see a page that belongs to that group alone. Without the second half, a
   role granted Batches and Attendance but not the separate `lms` key had
   both pages and no way to reach them. Entries listed under more than one
   group (Ranking Dashboard) do not count, or a student who has that one
   page would be shown the LMS and Trainer groups. */
export const canSeeMenuGroup = (group, hasPermission, isStudent) =>
  hasPermission(group.eventKey, 'menu') ||
  (group.content || []).some(
    (item) =>
      !SHARED_ITEM_KEYS.has(item.eventKey) &&
      (!item.studentOnly || isStudent) &&
      item.eventKey !== 'chat' &&
      canSeeMenuItem(item, hasPermission, isStudent)
  );

const asPath = (to) => `/${String(to || '').replace(/^\/+/, '')}`;

/* The first page in the sidebar this person can open, top to bottom -- or
   null. The dashboard itself and the entries everybody has (Notifications,
   Appearance, Chat) are passed over: this answers "where do I send someone
   the dashboard has nothing for". */
export const firstVisiblePage = (hasPermission, isStudent) => {
  const skipped = ['dashboard', 'notifications', 'appearance', 'chat'];
  for (const group of MenuList) {
    if (!group.eventKey || skipped.includes(group.eventKey)) continue;
    const items = group.content || [];
    if (items.length) {
      if (!canSeeMenuGroup(group, hasPermission, isStudent)) continue;
      const item = items.find(
        (it) => it.to && (!it.studentOnly || isStudent) && it.eventKey !== 'chat' && canSeeMenuItem(it, hasPermission, isStudent)
      );
      if (item) return asPath(item.to);
    } else if (group.to && hasPermission(group.eventKey, 'menu')) {
      return asPath(group.to);
    }
  }
  return null;
};
